import { useQuery, useQueryClient } from "react-query";
import { assert } from "#/utils/general";
import { Logger } from "#/lib/logging";
import { useSession } from "@supabase/auth-helpers-react";
import { useSupabase } from "./use-supbase";
import { useIsUserLoggedIn } from "#next/hooks/use-is-user-logged-in";
import { useRealtimeSubscription } from "#next/hooks/with-react-context/use-realtime-subscription/use-realtime-subscription";
import { TableRow } from "#/types/supabase";


export const useApiToken = (): string | null => {
  const session = useSession();
  const supabase = useSupabase();
  const { isLoggedIn } = useIsUserLoggedIn();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;
  const {
    data: apiToken,
    refetch: refetchApiToken
  } = useQuery<TableRow<"api_tokens"> | null>([ "api-token", userId ], async () => {
    assert(!!userId, "Session user ID is required");
    const {
      data: result,
      error: resultError
    } = await supabase.from("api_tokens")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    if (resultError) {
      throw Logger.error({
        message: "Error fetching API token",
        error: resultError
      });
    }
    if (!result) {
      return null;
    }
    return result;
  }, {
    enabled: !!isLoggedIn && !!userId
  });
  useRealtimeSubscription({
    table: "api_tokens",
    onInsert: async data => {
      if (data.new.user_id === session?.user?.id) {
        await refetchApiToken();
      }
    },
    onUpdate: async data => {
      if (data.new.user_id === session?.user?.id) {
        await refetchApiToken();
      }
    },
    onDelete: async data => {
      if (data.old.id === apiToken?.id) {
        queryClient.setQueryData([ "api-token", session?.user?.id ], null);
      }
    }
  });
  return apiToken?.token ?? null;
};